// Discount codes (Oct 2026): the rules a code follows, written once and used
// everywhere a code is made, shown, shared or used. Pure: no storage, no
// network, no React. The server will run the same checks (discount/validate);
// until it does, the device stand-in runs these. Plan:
// https://claude.ai/artifact/G7bFTBin9WSyuZH1oDu1bV
//
// A code:
// {
//   id, code: 'AUTUMN20', salonId,
//   ownerType: 'salon' | 'stylist', ownerId, ownerName,
//   status: 'live' | 'paused' | 'ended',
//   type: 'percentage' | 'fixed', value,               // 20 (%) or 10 (£)
//   fundingMode: 'shared' | 'salon' | 'stylist',       // who pays on self-employed lines
//   scope: { services: 'all' | 'some', serviceIds, categoryIds, names, products, productScope: 'all' | 'some', productIds, stylistIds },
//   audience: 'everyone' | 'new',
//   minSpend, totalUsesCap, perClientCap,
//   startsOn, endsOn, appointmentsBy,                  // 'YYYY-MM-DD', UK dates
//   days: [0..6],                                      // appointment weekdays, empty = any
//   channels: { online, salon },
//   stats: { uses, revenue, discountGiven, newClients },
//   history: [{ at, by, byName, action, note }],
// }
import moment from 'moment';

// ── Code strings ────────────────────────────────────────────────────────────

// Gift cards are BEU-XXXX-XXXX-XXXX, so no discount code may start with BEU.
export const RESERVED_PREFIX = 'BEU';
const GIFT_CARD_RE = /^\s*BEU[\s-]*[A-Z0-9]{4}[\s-]*[A-Z0-9]{4}([\s-]*[A-Z0-9]{4})?\s*$/i;
export const looksLikeGiftCard = (s) => GIFT_CARD_RE.test(String(s || ''));

export const MIN_LEN = 3;
export const MAX_LEN = 20;

// What the salon types, tidied: capitals and numbers only.
export const cleanCode = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, MAX_LEN);

// How two codes are compared: case, spaces and dashes don't matter, and the
// letters people mix up with numbers count as those numbers (O is 0, I and L
// are 1), so a client typing "autumn 2o" still finds AUTUMN20.
export const codeKey = (s) => cleanCode(s).replace(/O/g, '0').replace(/[IL]/g, '1');

// Why a code can't be saved as typed, or null.
export const codeProblem = (s, codes = [], selfId = null) => {
  const c = cleanCode(s);
  if (c.length < MIN_LEN || c.length > MAX_LEN) return `Use ${MIN_LEN} to ${MAX_LEN} letters or numbers.`;
  if (c.startsWith(RESERVED_PREFIX)) return 'Codes starting with BEU are kept for gift cards.';
  const k = codeKey(c);
  const clash = (codes || []).find((x) => x && x.id !== selfId && codeKey(x.code) === k);
  if (clash) return clash.code === c ? 'You already have this code.' : `This is too close to ${clash.code}, which you already have.`;
  return null;
};

// ── Money and dates ─────────────────────────────────────────────────────────

export const toPence = (n) => Math.round((Number(n) || 0) * 100);
export const fromPence = (p) => Math.round(p) / 100;

// £10, £10.50: whole pounds without the pennies
export const money = (n) => {
  const p = toPence(n);
  const neg = p < 0;
  const abs = Math.abs(p);
  const s = abs % 100 === 0 ? `£${(abs / 100).toLocaleString('en-GB')}` : `£${(abs / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return neg ? `−${s}` : s;
};
// £10.00, for totals and breakdowns
export const money2 = (n) => {
  const p = toPence(n);
  const s = `£${(Math.abs(p) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return p < 0 ? `−${s}` : s;
};

export const dayOf = (d) => (d ? moment(d).format('YYYY-MM-DD') : null);
const said = (ymd) => moment(ymd, 'YYYY-MM-DD').format('ddd D MMM');
const saidYear = (ymd, now) => {
  const m = moment(ymd, 'YYYY-MM-DD');
  return m.year() === moment(now).year() ? m.format('ddd D MMM') : m.format('ddd D MMM YYYY');
};
export const dateSaid = said;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK = [1, 2, 3, 4, 5, 6, 0]; // Monday first, as salons think of the week

const joinAnd = (xs) => (xs.length <= 1 ? (xs[0] || '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const joinOr = (xs) => (xs.length <= 1 ? (xs[0] || '') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);

// "Tuesdays and Wednesdays", "Monday to Thursday"
export const daysSaid = (days) => {
  const set = new Set((days || []).map(Number));
  if (!set.size || set.size === 7) return '';
  const order = WEEK.filter((d) => set.has(d));
  const idx = order.map((d) => WEEK.indexOf(d));
  const run = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (run && order.length >= 3) return `${DAY_NAMES[order[0]]} to ${DAY_NAMES[order[order.length - 1]]}`;
  return joinAnd(order.map((d) => `${DAY_NAMES[d]}s`));
};

// ── What a code is worth and what it covers ────────────────────────────────

export const valueSaid = (code) => {
  if (!code) return '';
  return code.type === 'fixed' ? `${money(code.value)} off` : `${Number(code.value) || 0}% off`;
};

// "Colour and Balayage", "all services", "3 services"
export const coversSaid = (code, { short } = {}) => {
  const s = (code && code.scope) || {};
  if (s.services !== 'some') return s.products ? (s.productScope === 'some' ? 'all services and chosen products' : 'everything') : 'all services';
  const names = [...(s.categoryIds || []), ...(s.serviceIds || [])].map((id) => (s.names || {})[id]).filter(Boolean);
  if (!names.length) return 'chosen services';
  if (names.length > (short ? 2 : 3)) return `${names.length} services`;
  return joinAnd(names);
};

// The headline: "20% off Colour and Balayage"
export const headlineOf = (code) => {
  if (!code) return '';
  const covers = coversSaid(code, { short: true });
  if (code.audience === 'new' && covers === 'all services') return `${valueSaid(code)} a first visit`;
  return `${valueSaid(code)} ${covers}`;
};

// ── State ───────────────────────────────────────────────────────────────────

export const usesOf = (code) => Number((code && code.stats && code.stats.uses) || 0);

// What the code is doing today: live, scheduled, paused, ended or all used
export const stateOf = (code, now = new Date()) => {
  if (!code) return 'ended';
  const today = dayOf(now);
  if (code.status === 'ended') return 'ended';
  if (code.endsOn && code.endsOn < today) return 'ended';
  if (code.totalUsesCap && usesOf(code) >= Number(code.totalUsesCap)) return 'used-up';
  if (code.status === 'paused') return 'paused';
  if (code.startsOn && code.startsOn > today) return 'scheduled';
  return 'live';
};

export const STATE_LABEL = { live: 'Live', scheduled: 'Scheduled', paused: 'Paused', ended: 'Ended', 'used-up': 'All used' };

// Can the code be put on a new post, email or text? (live or about to start)
export const shareable = (code, now = new Date()) => ['live', 'scheduled'].includes(stateOf(code, now));

// Once a code has been used its value and rules are fixed, so what a client
// was promised never changes under them. Status, the end date and the total
// number of uses can still move (with a note in the history).
export const lockedOnceUsed = (code) => usesOf(code) > 0;

// ── The small print ─────────────────────────────────────────────────────────

const stylistsSaid = (code) => {
  const s = (code && code.scope) || {};
  const names = (s.stylistIds || []).map((id) => (s.names || {})[id]).filter(Boolean);
  if (!names.length || code.ownerType === 'stylist') return '';
  return `With ${joinOr(names)} only`;
};

// Every condition a client needs, from the code's own settings (CAP Code 8.17:
// how to use it, dates, who it's for, limits). Goes on every post, email and
// text the code appears in, and on the booking page.
//   { long: true } adds the conditions that are true of every code
// shop: the online shop's wording (ends on a date; no appointments).
export const termsOf = (code, { now = new Date(), long = false, withCode = true, shop = false } = {}) => {
  if (!code) return '';
  const parts = [];
  const lead = `${valueSaid(code)} ${coversSaid(code)}`;
  parts.push(withCode ? `${lead} with ${code.code}.` : `${lead}.`);
  const dates = [];
  if (code.startsOn && code.startsOn > dayOf(now)) dates.push(`From ${saidYear(code.startsOn, now)}`);
  if (code.endsOn) dates.push(shop ? `${dates.length ? 'ends' : 'Ends'} ${saidYear(code.endsOn, now)}` : `${dates.length ? 'book' : 'Book'} by ${saidYear(code.endsOn, now)}`);
  if (code.appointmentsBy && !shop) dates.push(`for appointments by ${saidYear(code.appointmentsBy, now)}`);
  if (dates.length) parts.push(`${dates.join(', ').replace(/, (for appointments)/, ' $1')}.`);
  const days = daysSaid(code.days);
  if (days) parts.push(`${days} only.`);
  const who = [];
  if (code.audience === 'new') who.push('New clients only');
  const st = stylistsSaid(code);
  if (st) who.push(st);
  if (code.minSpend) who.push(`minimum spend ${money(code.minSpend)}`);
  if (code.perClientCap === 1) who.push('one use each');
  else if (code.perClientCap > 1) who.push(`${code.perClientCap} uses each`);
  if (who.length) parts.push(`${who[0][0].toUpperCase()}${who.join(', ').slice(1)}.`);
  const ch = code.channels || {};
  if (ch.online && !ch.salon && !shop) parts.push('Online bookings only.');
  if (!ch.online && ch.salon) parts.push('In person only.');
  if (code.totalUsesCap) parts.push(`Limited to ${code.totalUsesCap} uses.`);
  if (long) parts.push('One code per booking. Can’t be used with other offers or exchanged for cash.');
  return parts.join(' ');
};

// For a text, where every character counts: the code, the value and the
// conditions that matter most.
export const termsShort = (code, { now = new Date() } = {}) => {
  if (!code) return '';
  const bits = [`${code.code}: ${valueSaid(code)} ${coversSaid(code, { short: true })}`];
  if (code.endsOn) bits.push(`book by ${moment(code.endsOn, 'YYYY-MM-DD').format('D MMM')}`);
  const who = [];
  if (code.audience === 'new') who.push('new clients');
  if (code.minSpend) who.push(`min spend ${money(code.minSpend)}`);
  if (code.perClientCap === 1) who.push('1 use each');
  const days = daysSaid(code.days);
  if (days) who.push(`${days} only`);
  const tail = who.length ? `. ${who.join(', ').replace(/^./, (c) => c.toUpperCase())}` : '';
  const ch = code.channels || {};
  const where = ch.online && !ch.salon ? '. Online only' : (!ch.online && ch.salon ? '. In person only' : '');
  void now;
  return `${bits.join(', ')}${tail}${where}.`;
};

// ── A code on a post ────────────────────────────────────────────────────────

// The words an offer post takes from a live code (Social's Offer, the email
// Offer section): what it's worth, what it covers, when, the code itself and
// its small print. Never a price: the salon shows the full price themselves.
export const offerWords = (code, now = new Date()) => {
  if (!code) return {}
  const covers = coversSaid(code)
  const firstVisit = code.audience === 'new' && covers === 'all services'
  const days = daysSaid(code.days)
  const scheduled = code.startsOn && code.startsOn > dayOf(now)
  let dates = ''
  if (days) dates = code.endsOn ? `${days}, until ${said(code.endsOn)}` : days
  else if (code.endsOn) dates = `Book by ${said(code.endsOn)}`
  else if (scheduled) dates = `From ${said(code.startsOn)}`
  return {
    eyebrow: code.audience === 'new' ? 'For new clients' : (code.endsOn ? `Until ${said(code.endsOn)}` : 'Offer'),
    value: valueSaid(code),
    service: firstVisit ? 'your first visit' : covers,
    dates,
    code: code.code,
    codeId: code.id || '',
    small: termsOf(code, { now }),
  }
}

// ── Checking a code against a booking ───────────────────────────────────────

// The reasons a code can't be used. `client` is what the client sees; the
// salon side can say more (`salon`), such as who hasn't agreed.
export const REASONS = {
  not_found: 'That code isn’t right. Check it and try again.',
  gift_card: 'That’s a gift card code. Add it as a gift card instead.',
  paused: 'That code isn’t available at the moment.',
  not_started: (c) => `That code starts on ${said(c.startsOn)}.`,
  ended: (c) => (c.endsOn && c.status !== 'ended' ? `That code ended on ${said(c.endsOn)}.` : 'That code has ended.'),
  used_up: 'That code has been fully used.',
  online_only: 'That code is for online bookings only.',
  salon_only: 'That code can only be used in person.',
  appointment_after: (c) => `That code is for appointments up to ${said(c.appointmentsBy)}.`,
  day: (c) => {
    const d = daysSaid(c.days);
    return / to /.test(d) ? `That code is for appointments from ${d}.` : `That code is for appointments on ${d}.`;
  },
  new_only: 'That code is for new clients only.',
  used_before: (c) => (Number(c.perClientCap) > 1 ? `You’ve already used that code ${c.perClientCap} times.` : 'You’ve already used that code.'),
  stylist: (c) => {
    const s = (c && c.scope) || {};
    const names = (c.ownerType === 'stylist' ? [c.ownerName] : (s.stylistIds || []).map((id) => (s.names || {})[id])).filter(Boolean);
    return names.length ? `That code is for bookings with ${joinOr(names)}.` : 'That code doesn’t cover these services.';
  },
  no_lines: 'That code doesn’t cover these services.',
  min_spend: (c) => `Spend ${money(c.minSpend)} or more${c.scope && c.scope.services === 'some' ? ` on ${coversSaid(c, { short: true })}` : ''} to use that code.`,
};

export const reasonText = (reason, code) => {
  const r = REASONS[reason];
  if (!r) return REASONS.not_found;
  return typeof r === 'function' ? r(code || {}) : r;
};

// Whether a line's stylist lets this code touch it. Salon codes only reach a
// self-employed stylist's services once they've said yes to that kind of
// funding (Settings › Your choices); until then their lines are skipped.
//   consentOf(stylistId) -> { selfEmployed, salonPays, shared } | null
export const consentAllows = (code, line, consentOf) => {
  if (!code || code.ownerType === 'stylist') return true;
  if (line.kind === 'product') return true;
  const c = consentOf ? consentOf(line.stylistId) : null;
  if (!c || !c.selfEmployed) return true;
  if (code.fundingMode === 'salon') return !!(c.salonPays || c.shared);
  return !!c.shared;
};

// Is this line one the code covers? Returns null when it is, or why not.
export const lineProblem = (code, line, consentOf) => {
  const s = code.scope || {};
  // The salon's products: a stylist's own code never reaches them (the salon would pay).
  if (line.kind === 'product') {
    if (code.ownerType === 'stylist') return 'stylist';
    if (!s.products) return 'scope';
    if (s.productScope === 'some' && !(s.productIds || []).map(String).includes(String(line.serviceId))) return 'scope';
    return null;
  }
  if (line.kind && line.kind !== 'service') return 'scope'; // gift cards, deposits, fees, tips
  if (code.ownerType === 'stylist' && String(line.stylistId) !== String(code.ownerId)) return 'stylist';
  if (code.ownerType !== 'stylist' && (s.stylistIds || []).length && !s.stylistIds.map(String).includes(String(line.stylistId))) return 'stylist';
  if (s.services === 'some') {
    const inService = (s.serviceIds || []).map(String).includes(String(line.serviceId));
    const inCategory = (s.categoryIds || []).map(String).includes(String(line.categoryId));
    if (!inService && !inCategory) return 'scope';
  }
  if (!consentAllows(code, line, consentOf)) return 'consent';
  return null;
};

// Share `totalPence` across lines in proportion to their value, to the penny:
// floor every share, then hand the pennies left to the biggest remainders.
export const allocate = (totalPence, weights) => {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!sum || totalPence <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (totalPence * w) / sum);
  const out = raw.map((r) => Math.floor(r));
  let left = totalPence - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; left > 0 && k < order.length; k += 1, left -= 1) out[order[k][1]] += 1;
  return out;
};

/**
 * Check a code against a booking or a sale, and work out the saving.
 *
 *   ctx = {
 *     now,                        // when it's being used (booking time)
 *     date,                       // the appointment day ('YYYY-MM-DD'), today at the till
 *     channel: 'online' | 'salon',
 *     lines: [{ id, kind: 'service' | 'product', serviceId, categoryId, stylistId, price, qty }],
 *     client: { isNew: true | false | null, used: number },   // null = not known yet
 *     consentOf,                  // see consentAllows
 *   }
 *
 * -> { ok, reason, message, amount, subtotal, total, lines: [{ id, eligible, why, amount }] }
 *    Amounts in pounds. A failed check still says which lines it would cover.
 */
export const quote = (code, ctx = {}) => {
  const now = ctx.now || new Date();
  const lines = (ctx.lines || []).map((l) => ({ ...l, qty: Number(l.qty) || 1, pence: toPence(l.price) * (Number(l.qty) || 1) }));
  const subtotalP = lines.reduce((a, l) => a + l.pence, 0);
  const fail = (reason, extra = {}) => ({
    ok: false,
    reason,
    message: reasonText(reason, code),
    amount: 0,
    subtotal: fromPence(subtotalP),
    total: fromPence(subtotalP),
    lines: lines.map((l) => ({ id: l.id, eligible: false, why: extra.why || reason, amount: 0 })),
  });
  if (!code) return fail('not_found');
  const state = stateOf(code, now);
  if (state === 'ended') return fail('ended');
  if (state === 'used-up') return fail('used_up');
  if (state === 'paused') return fail('paused');
  if (state === 'scheduled') return fail('not_started');
  const ch = code.channels || { online: true, salon: true };
  if (ctx.channel === 'online' && ch.online === false) return fail('salon_only');
  if (ctx.channel === 'salon' && ch.salon === false) return fail('online_only');
  const day = ctx.date || dayOf(now);
  if (code.appointmentsBy && day > code.appointmentsBy) return fail('appointment_after');
  if ((code.days || []).length && code.days.length < 7 && !code.days.map(Number).includes(moment(day, 'YYYY-MM-DD').day())) return fail('day');
  const client = ctx.client || {};
  if (code.audience === 'new' && client.isNew === false) return fail('new_only');
  if (code.perClientCap && Number(client.used || 0) >= Number(code.perClientCap)) return fail('used_before');

  const checked = lines.map((l) => ({ ...l, why: lineProblem(code, l, ctx.consentOf) }));
  const ok = checked.filter((l) => !l.why && l.pence > 0);
  if (!ok.length) {
    const services = checked.filter((l) => l.kind !== 'product');
    const byStylist = services.length && services.every((l) => l.why === 'stylist');
    const res = fail(byStylist ? 'stylist' : 'no_lines');
    res.lines = checked.map((l) => ({ id: l.id, eligible: false, why: l.why, amount: 0 }));
    return res;
  }
  const eligibleP = ok.reduce((a, l) => a + l.pence, 0);
  if (code.minSpend && eligibleP < toPence(code.minSpend)) {
    const res = fail('min_spend');
    res.lines = checked.map((l) => ({ id: l.id, eligible: !l.why, why: l.why, amount: 0 }));
    return res;
  }

  let amounts;
  if (code.type === 'fixed') {
    amounts = allocate(Math.min(toPence(code.value), eligibleP), ok.map((l) => l.pence));
  } else {
    const pct = Math.max(0, Math.min(100, Number(code.value) || 0));
    amounts = ok.map((l) => Math.min(l.pence, Math.round((l.pence * pct) / 100)));
  }
  const byId = new Map(ok.map((l, i) => [l.id, amounts[i]]));
  const amountP = amounts.reduce((a, b) => a + b, 0);
  return {
    ok: true,
    reason: null,
    message: '',
    amount: fromPence(amountP),
    subtotal: fromPence(subtotalP),
    total: fromPence(subtotalP - amountP),
    eligibleSubtotal: fromPence(eligibleP),
    lines: checked.map((l) => ({ id: l.id, eligible: !l.why, why: l.why, amount: fromPence(byId.get(l.id) || 0) })),
  };
};

// ── Booking sums ────────────────────────────────────────────────────────────

// What the client pays and when, once a code is on. The deposit is never more
// than the discounted total (a fixed deposit stays as set otherwise). A gift
// card pays toward the deposit first, as it does today.
export const bookingSums = ({ subtotal = 0, discount = 0, deposit = 0, giftCard = 0 } = {}) => {
  const totalP = Math.max(0, toPence(subtotal) - toPence(discount));
  const depositP = Math.min(toPence(deposit), totalP);
  const giftP = depositP > 0 ? Math.min(toPence(giftCard), totalP) : 0;
  const todayP = Math.max(0, depositP - giftP);
  const laterP = Math.max(0, totalP - giftP - todayP);
  return { total: fromPence(totalP), deposit: fromPence(depositP), gift: fromPence(giftP), payToday: fromPence(todayP), payAtSalon: fromPence(laterP) };
};

// ── Who pays for it (self-employed) ─────────────────────────────────────────

// Each side's share of one line, given the salon's cut of the stylist's
// takings (0 to 1). `salon` can come out below zero: that's money the salon
// owes the stylist (shown in Commissions, as vouchers are), for example when
// the salon pays for a code on a chair renter's service.
export const splitFor = ({ price, discount, salonCut, mode }) => {
  const P = toPence(price);
  const D = Math.min(toPence(discount), P);
  const cut = Math.max(0, Math.min(1, Number(salonCut) || 0));
  const paid = P - D;
  let salon;
  if (mode === 'salon') salon = paid - Math.round(P * (1 - cut));
  else if (mode === 'stylist') salon = Math.round(P * cut);
  else salon = Math.round(paid * cut);
  return { salon: fromPence(salon), stylist: fromPence(paid - salon), paid: fromPence(paid) };
};

// ── Warnings in the editor (advice, never a block) ──────────────────────────

const sameScope = (a, b) => {
  const sa = a.scope || {};
  const sb = b.scope || {};
  if (sa.services !== 'some' || sb.services !== 'some') return sa.services !== 'some' || sb.services !== 'some';
  const ids = (s) => new Set([...(s.serviceIds || []), ...(s.categoryIds || [])].map(String));
  const A = ids(sa);
  return [...ids(sb)].some((x) => A.has(x));
};

// Days, in the 90 before `now`, on which a public code covered the same
// services (the ASA's DUSK ruling: codes running back to back make the full
// price look made-up).
export const codeDays = (draft, codes, now = new Date()) => {
  const from = moment(now).startOf('day').subtract(89, 'days');
  const days = new Set();
  (codes || []).filter((c) => c.id !== draft.id && c.audience !== 'new' && sameScope(draft, c)).forEach((c) => {
    const start = moment.max(from, moment(c.startsOn || c.createdAt || from, 'YYYY-MM-DD'));
    const endDay = c.status === 'ended' && c.endedOn ? c.endedOn : (c.endsOn || dayOf(now));
    const end = moment.min(moment(now).startOf('day'), moment(endDay, 'YYYY-MM-DD'));
    for (let d = start.clone(); !d.isAfter(end, 'day'); d.add(1, 'day')) days.add(d.format('YYYY-MM-DD'));
  });
  return days.size;
};

export const warningsFor = (draft, codes, now = new Date()) => {
  const out = [];
  if (!draft) return out;
  const today = dayOf(now);
  // The same offer again soon after one ended (a "limited time" that isn't)
  const recent = (codes || []).find((c) => c.id !== draft.id && c.type === draft.type && Number(c.value) === Number(draft.value)
    && sameScope(draft, c) && stateOf(c, now) === 'ended'
    && moment(today).diff(moment(c.endedOn || c.endsOn || today, 'YYYY-MM-DD'), 'days') <= 30);
  if (recent) {
    out.push({ id: 'relaunch', text: `${recent.code} offered the same until ${said(recent.endedOn || recent.endsOn)}. Running it again so soon can make the end date look made-up.` });
  }
  if (draft.audience !== 'new') {
    const n = codeDays(draft, codes, now);
    if (n >= 54) out.push({ id: 'back-to-back', text: `These services have had a code on ${n} of the last 90 days. If there's nearly always a code, the full price stops looking real.` });
  }
  if (draft.endsOn && draft.endsOn < today) out.push({ id: 'past', text: 'The book-by date has already passed.' });
  if (draft.appointmentsBy && draft.endsOn && draft.appointmentsBy < draft.endsOn) out.push({ id: 'appt-before-end', text: 'Appointments must be by a date before the book-by date, so the last days of the code can’t be used.' });
  return out;
};

// What has to be fixed before saving, or null. `codes` for the clash check.
export const draftProblems = (draft, codes = []) => {
  const p = {};
  const codeErr = codeProblem(draft.code, codes, draft.id);
  if (codeErr) p.code = codeErr;
  const v = Number(draft.value);
  if (!(v > 0)) p.value = draft.type === 'fixed' ? 'Enter an amount.' : 'Enter a percentage.';
  else if (draft.type === 'percentage' && v > 100) p.value = 'A percentage can’t be over 100.';
  else if (draft.type === 'percentage' && Math.round(v) !== v) p.value = 'Use a whole number.';
  const s = draft.scope || {};
  if (s.services === 'some' && !((s.serviceIds || []).length || (s.categoryIds || []).length)) p.scope = 'Choose at least one service.';
  if (s.products && s.productScope === 'some' && !(s.productIds || []).length) p.products = 'Choose at least one product.';
  if (draft.minSpend != null && draft.minSpend !== '' && !(Number(draft.minSpend) > 0)) p.minSpend = 'Enter an amount, or turn this off.';
  if (draft.totalUsesCap != null && draft.totalUsesCap !== '' && !(Number(draft.totalUsesCap) >= 1)) p.totalUsesCap = 'Enter a number, or turn this off.';
  if (draft.fundingMode === 'salon' && draft.ownerType !== 'stylist' && !(Number(draft.totalUsesCap) >= 1)) p.totalUsesCap = 'When the business owner pays, set a total so you know the most it can cost.';
  if (draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn) p.endsOn = 'The book-by date is before the start.';
  const ch = draft.channels || {};
  if (!ch.online && !ch.salon) p.channels = 'Choose where it can be used.';
  if (draft.ownerType === 'salon' && draft.fundingMode === 'stylist') p.fundingMode = 'The business owner’s code can’t be paid for by a stylist.';
  return Object.keys(p).length ? p : null;
};

// ── Making one up ───────────────────────────────────────────────────────────

const SEASONS = ['WINTER', 'WINTER', 'SPRING', 'SPRING', 'SPRING', 'SUMMER', 'SUMMER', 'SUMMER', 'AUTUMN', 'AUTUMN', 'AUTUMN', 'WINTER'];
// Crockford's alphabet: no I, L, O or U, so nothing is misread
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export const randomTail = (n = 2, rand = Math.random) => Array.from({ length: n }, () => CROCKFORD[Math.floor(rand() * 32)]).join('');

// Words for the time of year, by month (0 = January): the season, and the
// dates salons run offers around. Only words that suit the month.
const MONTH_WORDS = [
  ['NEWYEAR', 'JANUARY', 'WINTER', 'FRESHSTART'],
  ['VALENTINE', 'LOVE', 'WINTER', 'FEBRUARY'],
  ['SPRING', 'MOTHERSDAY', 'MARCH', 'BLOOM'],
  ['SPRING', 'EASTER', 'APRIL', 'BLOOM'],
  ['SPRING', 'MAY', 'BLOOM', 'GLOW'],
  ['SUMMER', 'FATHERSDAY', 'JUNE', 'SUNSHINE'],
  ['SUMMER', 'JULY', 'SUNSHINE', 'HOLIDAY'],
  ['SUMMER', 'AUGUST', 'HOLIDAY', 'SUNSHINE'],
  ['AUTUMN', 'BACKTOSCHOOL', 'SEPTEMBER', 'REFRESH'],
  ['AUTUMN', 'HALLOWEEN', 'OCTOBER', 'COSY'],
  ['AUTUMN', 'BLACKFRIDAY', 'NOVEMBER', 'PARTY'],
  ['CHRISTMAS', 'FESTIVE', 'PARTY', 'WINTER'],
];

// Every obvious code for this draft, best first: what it's for (new
// clients, midweek, a service), then the time of year, then a few that suit
// any offer. Each carries the value ("AUTUMN20"). Codes already in use and
// the gift card prefix are left out.
export const codeIdeas = (draft = {}, codes = [], { now = new Date() } = {}) => {
  const s = draft.scope || {};
  const first = [...(s.categoryIds || []), ...(s.serviceIds || [])].map((id) => (s.names || {})[id]).filter(Boolean)[0];
  // The first word of a service or category ("Colour Services" -> COLOUR)
  const word = (w) => String(w || '').trim().split(/\s+/)[0].toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10);
  const midweek = (draft.days || []).length && (draft.days || []).every((d) => [1, 2, 3, 4].includes(Number(d)));
  const words = [];
  if (draft.audience === 'new') words.push('WELCOME', 'HELLO', 'FIRSTVISIT', 'NEWCLIENT');
  if (midweek) words.push('MIDWEEK', 'WEEKDAY');
  if (s.services === 'some' && word(first).length >= 3) words.push(word(first));
  words.push(...MONTH_WORDS[moment(now).month()]);
  words.push('TREAT', 'PAMPER', 'GLOWUP', 'THANKYOU', 'VIP');
  const v = Number(draft.value) > 0 ? String(Math.round(Number(draft.value))) : '';
  const taken = new Set((codes || []).map((c) => codeKey(c.code)));
  const seen = new Set();
  return words.map((w) => cleanCode(`${w}${v}`)).filter((c) => {
    const k = codeKey(c);
    if (seen.has(k) || taken.has(k) || c.length < MIN_LEN || c.startsWith(RESERVED_PREFIX)) return false;
    seen.add(k);
    return true;
  });
};

// "Make one up": the next obvious code after the one showing, round and round
export const nextCodeIdea = (current, draft, codes = [], opts = {}) => {
  const list = codeIdeas(draft, codes, opts);
  if (!list.length) return suggestCode(draft, codes, opts);
  const i = list.findIndex((c) => codeKey(c) === codeKey(current));
  return list[(i + 1) % list.length];
};

export const suggestCode = (draft = {}, codes = [], { now = new Date(), rand = Math.random } = {}) => {
  const list = codeIdeas(draft, codes, { now });
  if (list.length) return list[0];
  const v = Number(draft.value) > 0 ? String(Math.round(Number(draft.value))) : '';
  return cleanCode(`OFFER${v}${randomTail(3, rand)}`);
};

// ── Blank codes and ready-made ideas ────────────────────────────────────────

export const blankCode = ({ ownerType = 'salon', ownerId = null, ownerName = '', salonId = null } = {}, now = new Date()) => ({
  code: '',
  salonId,
  ownerType,
  ownerId,
  ownerName,
  status: 'live',
  type: 'percentage',
  value: 10,
  fundingMode: ownerType === 'stylist' ? 'stylist' : 'shared',
  scope: { services: 'all', serviceIds: [], categoryIds: [], names: {}, products: false, productScope: 'all', productIds: [], stylistIds: ownerType === 'stylist' && ownerId ? [String(ownerId)] : [] },
  audience: 'everyone',
  minSpend: null,
  totalUsesCap: null,
  perClientCap: 1,
  startsOn: dayOf(now),
  endsOn: null,
  appointmentsBy: null,
  days: [],
  channels: { online: true, salon: true },
  stats: { uses: 0, revenue: 0, discountGiven: 0, newClients: 0 },
  history: [],
});

// Ideas on the empty page. Each opens the editor filled in; nothing is
// published until the salon saves it.
export const IDEAS = [
  { id: 'welcome', title: 'Welcome', said: '£10 off a first visit', patch: { type: 'fixed', value: 10, audience: 'new', code: 'WELCOME10' } },
  { id: 'midweek', title: 'Midweek', said: '15% off Monday to Thursday', patch: { type: 'percentage', value: 15, days: [1, 2, 3, 4], code: 'MIDWEEK15' } },
  { id: 'season', title: 'This season', said: '20% off for a month', patch: { type: 'percentage', value: 20 }, season: true, weeks: 4 },
  { id: 'comeback', title: 'Come back', said: '15% off for clients you miss', patch: { type: 'percentage', value: 15, code: 'MISSYOU15', channels: { online: true, salon: true } }, weeks: 6 },
];

export const draftFromIdea = (idea, base, codes = [], now = new Date()) => {
  const d = { ...base, ...idea.patch, scope: { ...base.scope }, channels: { ...(idea.patch.channels || base.channels) } };
  if (idea.weeks) d.endsOn = moment(now).add(idea.weeks, 'weeks').format('YYYY-MM-DD');
  if (!d.code || codeProblem(d.code, codes)) d.code = suggestCode(d, codes, { now });
  return d;
};

// ── History ─────────────────────────────────────────────────────────────────

export const withHistory = (code, entry, now = new Date()) => ({
  ...code,
  history: [...(code.history || []), { at: moment(now).toISOString(), ...entry }],
});

export const HISTORY_SAID = {
  created: 'Made',
  edited: 'Changed',
  paused: 'Paused',
  resumed: 'Turned back on',
  ended: 'Ended',
  'end-moved': 'Book-by date moved',
  'cap-changed': 'Total uses changed',
};

// ── Links ───────────────────────────────────────────────────────────────────

// The booking link that applies the code: the salon's page for a salon code,
// the stylist's own page for theirs (/book/<salon>/professional/<id> counts
// as the stylist's own link, as /book/pro/<slug> does).
export const codeLink = ({ web, salonSlug, stylistId, code, src }) => {
  const base = String(web || '/').replace(/\/?$/, '/');
  if (!salonSlug || !code) return '';
  const path = stylistId ? `book/${salonSlug}/professional/${stylistId}` : `book/${salonSlug}`;
  const q = [`code=${encodeURIComponent(cleanCode(code))}`];
  if (src) q.push(`src=${encodeURIComponent(src)}`);
  return `${base}${path}?${q.join('&')}`;
};
